/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import Axios, { type InternalAxiosRequestConfig } from 'axios';
function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const session = localStorage.getItem('sessionInfo');
  const sessionObject = session
    ? (JSON.parse(session) as {
        username: string;
        sessionToken: string;
      })
    : undefined;
  if (sessionObject) {
    config.headers.Authorization = `Bearer ${sessionObject.sessionToken}`;
  }
  config.withCredentials = true;
  return config;
}
/**
 * Scope every request carries, split by the route level that owns it: `studio_code`
 * from the `studios/:studioCode` loader, `game_code` from the `:gameCode` loader, `sandbox_code` from the `:sandboxCode` loader
 * and cleared by the game-level dashboard; both lower slots are cleared again on
 * the studio dashboard and the home page, which sit above any Game. A request aimed at another scope (a new
 * Sandbox's first language, editing a Sandbox from the game-level list) overrides
 * either key with its own `params` — axios merges them key-by-key, request wins,
 * and an unset slot serialises to nothing.
 */
const scope: {
  studio_code?: string;
  game_code?: string;
  sandbox_code?: string;
} = {};

/** Called with no argument on the home page, which sits above every Studio. */
export const setStudioScope = (studioCode?: string) => {
  scope.studio_code = studioCode;
};

/** Called with no argument above the Game level. */
export const setGameScope = (gameCode?: string) => {
  scope.game_code = gameCode;
};

/**
 * Set the sandbox scope
 * @param sandboxCode
 * @description Called with no argument on game-level routes, which have no sandbox.
 */
export const setSandboxScope = (sandboxCode?: string) => {
  scope.sandbox_code = sandboxCode;
};

/**
 * Pass as a request's `params` to send it without the scope: the request wins key
 * by key, and an unset key serialises to nothing. For reads above every scope.
 */
export const UNSCOPED = {
  studio_code: undefined,
  game_code: undefined,
  sandbox_code: undefined,
} as const;

function scopeRequestInterceptor(config: InternalAxiosRequestConfig) {
  config.params = { ...scope, ...(config.params as Record<string, unknown>) };
  return config;
}

export const api = Axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_URL,
});
api.interceptors.request.use(authRequestInterceptor);
api.interceptors.request.use(scopeRequestInterceptor);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      alert(
        'Your session has expired. You will be redirected to the login screen.',
      );
      if (!currentPath.includes('/login') && currentPath !== '/logout') {
        window.location.href = `/login?from=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  },
);
